import { useCallback, useEffect, useRef, type MouseEvent, type PointerEvent } from 'react';

interface LongPressOptions {
	/** Hold time in ms (iOS context menus open at about 450ms). */
	delay?: number;
	/** How far the finger may drift before it counts as a scroll, in px. */
	tolerance?: number;
	disabled?: boolean;
}

/**
 * Classes for an element with a long-press action: no text selection and no
 * iOS callout (copy / look up) fighting the press. Don't put these on text
 * people need to select and copy; give that text a "Copy" action instead.
 */
export const LONG_PRESS_CLASS = 'select-none [-webkit-touch-callout:none]';

let disarmReleaseClick: (() => void) | null = null;

/**
 * When the finger lifts after a long press, the browser still sends a click
 * to whatever is under it. By then that is usually the sheet the long press
 * opened, so the click would pick a sheet item or hit Cancel. Drop that one
 * click, wherever it lands, then stop listening shortly after the release (or
 * as soon as a new touch starts). `onDisarm` runs when the guard ends.
 */
function swallowReleaseClick(onDisarm: () => void) {
	disarmReleaseClick?.();
	let timer: ReturnType<typeof setTimeout>;
	const disarm = () => {
		clearTimeout(timer);
		document.removeEventListener('click', onClick, true);
		document.removeEventListener('pointerup', onRelease, true);
		document.removeEventListener('pointercancel', onRelease, true);
		document.removeEventListener('pointerdown', disarm, true);
		if (disarmReleaseClick === disarm) disarmReleaseClick = null;
		onDisarm();
	};
	function onClick(event: globalThis.MouseEvent) {
		event.preventDefault();
		event.stopPropagation();
		disarm();
	}
	function onRelease() {
		document.removeEventListener('pointerup', onRelease, true);
		document.removeEventListener('pointercancel', onRelease, true);
		document.addEventListener('pointerdown', disarm, true);
		clearTimeout(timer);
		timer = setTimeout(disarm, 350);
	}
	document.addEventListener('click', onClick, true);
	document.addEventListener('pointerup', onRelease, true);
	document.addEventListener('pointercancel', onRelease, true);
	// Safety net in case the release never reaches the document.
	timer = setTimeout(disarm, 10000);
	disarmReleaseClick = disarm;
}

/**
 * Long-press on touch screens (mouse presses are ignored; desktop keeps its
 * right-click and hover menus). Spread the returned handlers on the element.
 * The click that ends a long press is swallowed, so neither the row nor the
 * sheet that opened under the finger reacts to it.
 *
 * Long-press is a shortcut, never the only way in: the same actions need a
 * visible button too (VoiceOver users can't long-press).
 */
export function useLongPress(onLongPress: (target: HTMLElement) => void, options: LongPressOptions = {}) {
	const { delay = 450, tolerance = 10, disabled = false } = options;
	const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const start = useRef<{ x: number; y: number } | null>(null);
	const fired = useRef(false);
	const callback = useRef(onLongPress);
	callback.current = onLongPress;

	const cancel = useCallback(() => {
		clearTimeout(timer.current);
		start.current = null;
	}, []);

	useEffect(() => cancel, [cancel]);

	const onPointerDown = useCallback(
		(event: PointerEvent<HTMLElement>) => {
			fired.current = false;
			if (disabled || event.pointerType === 'mouse' || !event.isPrimary) return;
			const target = event.currentTarget;
			start.current = { x: event.clientX, y: event.clientY };
			clearTimeout(timer.current);
			timer.current = setTimeout(() => {
				start.current = null;
				fired.current = true;
				navigator.vibrate?.(10);
				// Once the release click is gone, the row takes clicks again (a
				// keyboard Enter or Switch Control click has no pointerdown first).
				swallowReleaseClick(() => {
					fired.current = false;
				});
				callback.current(target);
			}, delay);
		},
		[delay, disabled],
	);

	const onPointerMove = useCallback(
		(event: PointerEvent<HTMLElement>) => {
			const origin = start.current;
			if (!origin) return;
			if (Math.hypot(event.clientX - origin.x, event.clientY - origin.y) > tolerance) cancel();
		},
		[cancel, tolerance],
	);

	// Android fires contextmenu on a long press; keep the browser menu away.
	const onContextMenu = useCallback(
		(event: MouseEvent<HTMLElement>) => {
			if (disabled || (event.nativeEvent as globalThis.PointerEvent).pointerType === 'mouse') return;
			if (start.current || fired.current) event.preventDefault();
		},
		[disabled],
	);

	return {
		onPointerDown,
		onPointerMove,
		onPointerUp: cancel,
		onPointerCancel: cancel,
		onPointerLeave: cancel,
		onContextMenu,
	};
}
